import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, TrendingUp, Mail, Calendar, CheckCircle2, Circle, ArrowRight } from "lucide-react";

function SetupChecklist() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/setup/checklist").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.complete) return null;
  const pct = Math.round((data.done / data.total) * 100);
  return (
    <div className="mt-8 rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="setup-checklist">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Get set up</div>
          <div className="font-serif-display text-2xl mt-1">{data.done === 0 ? "Go live in about 5 minutes." : `${data.done} of ${data.total} done — almost there.`}</div>
        </div>
        <div className="text-sm text-[#5c5c5c]" data-testid="setup-progress">{pct}%</div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-[#ebe8e0] overflow-hidden">
        <div className="h-full bg-[#163325] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-5 space-y-2">
        {data.steps.map((s) => (
          <li key={s.id}>
            <Link
              to={s.href}
              data-testid={`setup-step-${s.id}`}
              className={`group flex items-center gap-3 rounded-md px-3 py-2.5 -mx-3 transition-colors ${s.done ? "text-[#8c9b86]" : "hover:bg-[#f9f8f6]"}`}
            >
              {s.done ? <CheckCircle2 className="w-[18px] h-[18px] text-[#163325] shrink-0" /> : <Circle className="w-[18px] h-[18px] text-[#d4a373] shrink-0" />}
              <span className={`text-sm flex-1 ${s.done ? "line-through" : ""}`}>{s.label}</span>
              {!s.done && <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#163325]" />}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

const StatCard = ({ label, value, sub, icon: Icon, testId }) => (
  <div className="rounded-lg border border-[#e5e2da] bg-white p-5" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#5c5c5c]">{label}</div>
        <div className="font-serif-display text-4xl mt-2 text-[#163325]">{value}</div>
        {sub && <div className="text-xs text-[#5c5c5c] mt-1">{sub}</div>}
      </div>
      <Icon className="w-5 h-5 text-[#8c9b86]" strokeWidth={1.5} />
    </div>
  </div>
);

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/metrics/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 lg:p-10" data-testid="dashboard-overview">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#8c9b86]">Dashboard</div>
          <h1 className="font-serif-display text-4xl mt-2">Overview</h1>
        </div>
      </div>

      <SetupChecklist />

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total leads" value={data?.total_leads ?? "–"} sub={`${data?.qualification_rate ?? 0}% qualified`} icon={Users} testId="stat-total-leads" />
        <StatCard label="Chat sessions" value={data?.total_sessions ?? "–"} sub={`${data?.conversion_rate ?? 0}% → booked`} icon={TrendingUp} testId="stat-sessions" />
        <StatCard label="Booked calls" value={data?.booked_calls ?? "–"} sub="Ready to close" icon={Calendar} testId="stat-booked" />
        <StatCard label="Emails" value={data?.emails_sent ?? "–"} sub={`${data?.emails_scheduled ?? 0} scheduled`} icon={Mail} testId="stat-emails" />
      </div>

      <div className="mt-8 rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="leads-chart-card">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#5c5c5c]">Last 14 days</div>
            <div className="font-serif-display text-2xl mt-1">Leads captured</div>
          </div>
        </div>
        <div className="h-60 min-h-[240px]">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data?.series || []} margin={{ left: -10, right: 20, top: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ebe8e0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5c5c5c" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#5c5c5c" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e2da", background: "#fff", fontSize: 12 }} />
              <Line type="monotone" dataKey="leads" stroke="#163325" strokeWidth={2} dot={{ r: 3, fill: "#d4a373", strokeWidth: 0 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="quick-tip-card">
          <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Coach tip</div>
          <div className="font-serif-display text-2xl mt-2">Reply within 2 hours to warm leads.</div>
          <p className="mt-2 text-sm text-[#5c5c5c]">Coachly captures the lead 24/7, but a personal note in the same day still doubles booking rates. Try adding a short voice note reply on hot leads.</p>
        </div>
        <Link to="/app/settings#install-widget" className="rounded-lg border border-[#e5e2da] p-6 block hover:opacity-95 transition-opacity" style={{ background: "#163325", color: "#f9f8f6" }} data-testid="install-widget-card">
          <div className="text-xs uppercase tracking-widest text-[#d4a373]">Install widget</div>
          <div className="font-serif-display text-2xl mt-2">Add Coachly to your site.</div>
          <p className="mt-2 text-sm opacity-80">Paste one script tag into your site's &lt;head&gt; and Coachly is live in under 5 minutes.</p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#d4a373]">Get your snippet <ArrowRight className="w-4 h-4" /></div>
        </Link>
      </div>
    </div>
  );
}
